import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from '../../components/dashboard/DashboardLayout';
import { Button } from '../../components/Button';
import { Badge } from '../../components/Badge';
import { Modal } from '../../components/Modal';
import { toast } from 'react-hot-toast';
import { apiClient } from '../../api/client';

const UserProfilePage: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [showResetModal, setShowResetModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  // Refresh user data when component loads to get latest verification status
  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const handleResendVerification = async () => {
    try {
      setResendLoading(true);
      await apiClient.post('/api/auth/resend-verification');
      toast.success('Verification email sent! Check your inbox.');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to send verification email');
    } finally {
      setResendLoading(false);
    }
  };

  const handleResetPassword = () => {
    setShowResetModal(true);
  };

  const handleRequestPasswordReset = async () => {
    if (!user?.email) return;
    
    try {
      setLoading(true);
      await apiClient.post('/api/auth/forgot-password', { email: user.email });
      toast.success('Password reset email sent! Check your inbox.');
      setShowResetModal(false);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <DashboardLayout currentPage="profile">
      <div className="max-w-6xl mx-auto">
        <h1 className="heading-1 mb-2">Profile</h1>
        <p className="text-[--clinqr-text-secondary] mb-8">Manage your account settings</p>

        <div className="bg-white rounded-xl border border-[#E5E7EB] p-6 space-y-6 shadow-sm">
          {/* Email Section */}
          <div>
            <label className="block text-sm font-medium text-[#374151] mb-2">Email</label>
            <p className="text-lg">{user.email}</p>
          </div>

          {/* Google Account Connection */}
          {user.google_id && (
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-2">Sign-in Method</label>
              <Badge variant="success" className="!gap-1.5">
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Connected with Google account
              </Badge>
            </div>
          )}

          {/* Verification Status */}
          <div>
            <label className="block text-sm font-medium text-[#374151] mb-2">Email Verification</label>
            <div className="flex items-center gap-4">
              {user.is_verified ? (
                <Badge variant="success">✓ Verified</Badge>
              ) : (
                <>
                  <Badge variant="warning">⚠ Not Verified</Badge>
                  <Button
                    onClick={handleResendVerification}
                    disabled={resendLoading}
                    variant="secondary"
                    size="sm"
                  >
                    {resendLoading ? 'Sending...' : 'Resend Verification Email'}
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Account Created */}
          <div>
            <label className="block text-sm font-medium text-[#374151] mb-2">Member Since</label>
            <p className="text-lg">
              {new Date(user.created_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          </div>

          {/* Password Section */}
          <div className="pt-6 border-t">
            <h2 className="text-lg font-semibold text-[#111827] mb-4">Security</h2>
            <Button onClick={handleResetPassword} variant="secondary">
              Reset Password
            </Button>
          </div>
        </div>

        {/* Reset Password Modal */}
        <Modal
          isOpen={showResetModal}
          onClose={() => setShowResetModal(false)}
          title="Reset Password"
        >
          <div className="space-y-4">
            <p className="text-gray-600">
              We'll send a password reset link to <strong>{user.email}</strong>. 
              Click the link in the email to create a new password.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                onClick={() => setShowResetModal(false)}
                variant="secondary"
              >
                Cancel
              </Button>
              <Button
                onClick={handleRequestPasswordReset}
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </DashboardLayout>
  );
};

export default UserProfilePage;

